import { type Application } from '@/portal-api.definition';
import { ChevronDown } from '@nosinovacao/fe-icons';
import { Button, cn, If, useI18n, useImgSrcWithFallback } from '@nosinovacao/nosid-mfe-common';
import { useState } from 'react';

export function AppCardLight({ app }: Readonly<{ app: Application }>) {
  const { handleError, src } = useImgSrcWithFallback(
    app.LogoUrl,
    '/images/nos/default-app-image.svg',
  );
  const [expanded, setExpanded] = useState(false);
  const { t } = useI18n();
  const inactive = !app.IsAuthorized && !app.CanRequestAccess;
  const appNotIncluded = inactive && !app.AdminUsersOnly;
  const adminOnly = inactive && app.AdminUsersOnly;
  const appLink = getAppLink(app);
  return (
    <div className="grid rounded bg-nos-neutral-75 pb-4 align-middle [&>*]:px-3 [&>:first-child]:pt-4">
      <button
        data-tid="button-app-expand"
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="grid grid-cols-[auto_1fr_auto] items-center gap-3"
      >
        <img
          data-inactive={inactive}
          className="size-8 rounded-lg border-[0.5px] border-nos-neutral-200 object-cover data-[inactive=true]:opacity-50"
          alt={t('Apps.ApplicationIconAlt', { name: app.Name })}
          src={src}
          onError={handleError}
        />
        <div className="grid gap-1 text-start">
          <span
            data-tid="text-app-name"
            className="font-medium text-nos-neutral-1000 font-title-h5"
          >
            {app.Name}
          </span>
          <span
            data-tid="text-app-status"
            className={cn(
              'font-ui-9',
              app.IsAuthorized ? 'text-nos-green' : 'text-nos-neutral-500',
            )}
          >
            <If condition={app.IsAuthorized}>{t('Apps.Authorized')}</If>
            <If condition={appNotIncluded}>{t('Apps.AppNotAvailable')}</If>
            <If condition={adminOnly}>{t('Apps.AppAdminOnly')}</If>
          </span>
        </div>
        <ChevronDown
          data-expanded={expanded}
          width={24}
          height={24}
          className="text-nos-neutral-1000 transition-transform data-[expanded=true]:rotate-180"
        />
      </button>
      <div
        className="grid grid-rows-[0fr] overflow-hidden transition-all data-[expanded=true]:grid-rows-[1fr] data-[expanded=true]:pt-4"
        data-expanded={expanded}
        aria-hidden={!expanded}
      >
        <div className="grid min-h-0 justify-items-start gap-4">
          <span
            data-tid="text-app-description"
            className="font-light text-nos-neutral-900 font-text-p2"
          >
            {app.Description || t('Apps.NoDescription')}
          </span>
          <If condition={!!appLink}>
            <Button data-tid="button-app-learn-more" skin="light" variant="tertiary" asChild>
              <a target="_blank" rel="noopener noreferrer" href={appLink}>
                {t('Apps.LearnMore', { appName: app.Name })}
              </a>
            </Button>
          </If>
        </div>
      </div>
    </div>
  );
}

function getPlatform(): 'ios' | 'android' | 'web' {
  const userAgent = globalThis.navigator?.userAgent || '';
  const isAndroid = /android/i.test(userAgent);
  const isIOS =
    /iPad|iPhone|iPod/.test(userAgent) &&
    globalThis.window !== undefined &&
    !('MSStream' in globalThis.window);
  if (isAndroid) return 'android';
  if (isIOS) return 'ios';
  return 'web';
}

function getAppLink(app: Application) {
  const platform = getPlatform();
  switch (platform) {
    case 'android':
      return app.Clients?.find((c) => c.Type === 'Android')?.WebPageUrl || app.WebPageUrl;
    case 'ios':
      return app.Clients?.find((c) => c.Type === 'iOS')?.WebPageUrl || app.WebPageUrl;
    default:
      return app.WebPageUrl;
  }
}
